import { CSSProperties, FC } from "react";
import { makeStyles } from "tss-react/mui";
import { LoaderType } from "@miplatform/types";
import { Box, CircularProgress, LinearProgress, alpha } from "@mui/material";

interface MiLoaderProps {
  isLoading: boolean;
  loaderColor?: string;
  type?: LoaderType;
  width?: number | string;
  size?: number;
  style?: CSSProperties;
  testId?: string;
}

const getBackground = (
  color: string | undefined,
  type: LoaderType | undefined,
  primaryColor: string
) => {
  if (type !== "linear") return "transparent";
  return alpha(color || primaryColor, 0.2);
};

export const MiLoader: FC<MiLoaderProps> = ({
  isLoading,
  loaderColor,
  type = LoaderType.Circular,
  width,
  size = 30,
  style,
  testId = "mi-loader",
}) => {
  const { classes } = useStyles({ loaderColor, type, width, size });

  return (
    <Box className={classes.rootContainer} style={style} data-testid={testId}>
      {type === "linear" ? (
        <Box height={5} width="100%">
          {isLoading && (
            <LinearProgress
              className={classes.loaderProgress}
              data-testid="linear-progress"
              variant="indeterminate"
            />
          )}
        </Box>
      ) : (
        isLoading && (
          <CircularProgress
            style={{ color: loaderColor }}
            className={classes.loaderProgress}
            data-testid="circular-progress"
            variant="indeterminate"
            size={size}
          />
        )
      )}
    </Box>
  );
};

const useStyles = makeStyles<{
  loaderColor?: string;
  type?: LoaderType;
  width?: number | string;
  size?: number;
}>()((theme, props) => ({
  rootContainer: {
    width: props.width || "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loaderProgress: {
    width: "100%",
    height: "inherit",
    borderRadius: 5,
    backgroundColor: getBackground(
      props.loaderColor,
      props.type,
      theme.palette.primary.main
    ),
    color: alpha(props.loaderColor || theme.palette.primary.main, 0.2),
  },
}));
